import { useQuery } from '@tanstack/react-query'
import { api } from '../../services/api'
import { Badge, Card, EmptyState, Spinner, StatTile, Table } from '../../components/ui'
import { clp, formatDateShort, PAYMENT_METHOD_LABELS } from '../../utils/format'
import type { ChargeStatus, PaymentMethod } from '../../types/api'

interface Billing {
  charges: Array<{ id: string; description: string; amount: number; status: ChargeStatus; createdAt: string; taxDocumentId?: string | null; paidAmount: number }>
  documents: Array<{
    id: string
    type: string
    status: 'DRAFT' | 'ISSUED' | 'VOIDED'
    internalFolio: number
    description: string
    totalAmount: number
    issuedAt?: string | null
    createdAt: string
    charges: Array<{ id: string; description: string; amount: number }>
  }>
  payments: Array<{ id: string; receiptNumber: number; amount: number; method: PaymentMethod; paidAt: string }>
}

const CHARGE_STATUS: Record<string, { label: string; className: string }> = {
  PENDING: { label: 'Pendiente', className: 'bg-amber-100 text-amber-800' },
  PARTIALLY_PAID: { label: 'Pago parcial', className: 'bg-sky-100 text-sky-800' },
  PAID: { label: 'Pagado', className: 'bg-emerald-100 text-emerald-800' },
  CANCELLED: { label: 'Anulado', className: 'bg-gray-200 text-gray-600' },
}

const DOC_STATUS: Record<string, { label: string; className: string }> = {
  DRAFT: { label: 'Borrador', className: 'bg-gray-100 text-gray-600' },
  ISSUED: { label: 'Emitido', className: 'bg-emerald-100 text-emerald-800' },
  VOIDED: { label: 'Anulado', className: 'bg-red-100 text-red-700' },
}

/** Facturación consolidada de la empresa: cargos, documentos y pagos. */
export function PortalBillingPage() {
  const billing = useQuery({
    queryKey: ['portal-billing'],
    queryFn: async () => (await api.get<Billing>('/portal/billing')).data,
  })

  if (billing.isLoading) return <Spinner />
  const d = billing.data
  const openCharges = (d?.charges ?? []).filter((c) => c.status === 'PENDING' || c.status === 'PARTIALLY_PAID')
  const openTotal = openCharges.reduce((s, c) => s + c.amount - c.paidAmount, 0)
  const paidTotal = (d?.payments ?? []).reduce((s, p) => s + p.amount, 0)

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-display text-2xl font-bold text-ink">Facturación</h1>
        <p className="text-sm text-gray-500">Cargos por exámenes, documentos consolidados y pagos registrados.</p>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
        <StatTile label="Saldo por pagar" value={clp(openTotal)} accent={openTotal > 0 ? 'danger' : 'success'} />
        <StatTile label="Cargos abiertos" value={openCharges.length} accent="ink" />
        <StatTile label="Total pagado" value={clp(paidTotal)} accent="brand" />
      </div>

      <Card title="Cargos">
        {(d?.charges.length ?? 0) === 0 ? (
          <EmptyState message="Sin cargos registrados: se generan al completarse cada examen." />
        ) : (
          <Table headers={['Detalle', 'Fecha', 'Monto', 'Pagado', 'Estado']}>
            {d!.charges.map((c) => (
              <tr key={c.id}>
                <td className="px-2 py-2">{c.description}</td>
                <td className="px-2 py-2 text-gray-500">{formatDateShort(c.createdAt)}</td>
                <td className="px-2 py-2 font-medium">{clp(c.amount)}</td>
                <td className="px-2 py-2 text-gray-600">{c.paidAmount > 0 ? clp(c.paidAmount) : '—'}</td>
                <td className="px-2 py-2">
                  <Badge className={CHARGE_STATUS[c.status].className}>{CHARGE_STATUS[c.status].label}</Badge>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <Card title="Documentos tributarios">
        {(d?.documents.length ?? 0) === 0 ? (
          <EmptyState message="Sin documentos: la facturación consolidada agrupa los cargos del periodo." />
        ) : (
          <div className="space-y-2">
            {d!.documents.map((doc) => (
              <div key={doc.id} className="rounded-lg border border-gray-200 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-medium text-ink">
                    <span className="font-mono text-xs text-gray-500">#{doc.internalFolio}</span> {doc.description}
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-ink">{clp(doc.totalAmount)}</span>
                    <Badge className={DOC_STATUS[doc.status].className}>{DOC_STATUS[doc.status].label}</Badge>
                  </div>
                </div>
                {doc.charges.length > 0 && (
                  <p className="mt-1 text-xs text-gray-500">
                    Incluye: {doc.charges.map((c) => c.description).join(' · ')}
                  </p>
                )}
                <p className="mt-0.5 text-xs text-gray-400">
                  {doc.issuedAt ? `Emitido el ${formatDateShort(doc.issuedAt)}` : `Creado el ${formatDateShort(doc.createdAt)}`}
                </p>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card title="Pagos registrados">
        {(d?.payments.length ?? 0) === 0 ? (
          <EmptyState message="Sin pagos registrados todavía." />
        ) : (
          <Table headers={['Folio', 'Fecha', 'Medio', 'Monto']}>
            {d!.payments.map((p) => (
              <tr key={p.id}>
                <td className="px-2 py-2 font-mono text-xs">#{p.receiptNumber}</td>
                <td className="px-2 py-2 text-gray-500">{formatDateShort(p.paidAt)}</td>
                <td className="px-2 py-2">{PAYMENT_METHOD_LABELS[p.method]}</td>
                <td className="px-2 py-2 font-medium">{clp(p.amount)}</td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
    </div>
  )
}
